import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signInWithPopup, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc, getDoc, collection, getDocs } from 'firebase/firestore';
import { auth, googleProvider, db } from '../firebase';
import { useAuthStore } from '../store/authStore';
import { createAppUser, createBasicSubscription } from '../lib/authSession';

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, setUser, setSubscription } = useAuthStore();
  const [isRegister, setIsRegister] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // If user becomes set via onAuthStateChanged (e.g. after popup), navigate to dashboard
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let firebaseUser;
      if (isRegister) {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        firebaseUser = cred.user;
      } else {
        const cred = await signInWithEmailAndPassword(auth, email, password);
        firebaseUser = cred.user;
      }

      const token = await firebaseUser.getIdToken();
      localStorage.setItem('token', token);
      await syncUserToFirestore(firebaseUser);
      setUser(createAppUser(firebaseUser));
      setSubscription(createBasicSubscription());
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const syncUserToFirestore = async (firebaseUser: any) => {
    const appUser = createAppUser(firebaseUser);
    const subscription = createBasicSubscription();
    const userRef = doc(db, 'users', appUser.firebase_uid);
    const existingSnap = await getDoc(userRef);
    const now = new Date().toISOString();
    await setDoc(userRef, {
      uid: appUser.firebase_uid,
      id: appUser.id,
      email: appUser.email,
      display_name: appUser.display_name || null,
      photo_url: appUser.photo_url || null,
      preferred_language: appUser.preferred_language,
      plan_name: 'Basic',
      status: 'active',
      last_login: now,
      created_at: existingSnap.exists() ? (existingSnap.data().created_at || now) : now,
    }, { merge: true });

    const subsQuery = await getDocs(collection(db, 'users', appUser.firebase_uid, 'subscriptions'));
    if (subsQuery.empty) {
      const subRef = doc(collection(db, 'users', appUser.firebase_uid, 'subscriptions'));
      await setDoc(subRef, {
        plan_id: 2,
        plan_name: 'Basic',
        status: 'active',
        start_date: now,
        end_date: subscription.end_date,
        created_at: now,
      });
    }
  };

  const handleGoogle = async () => {
    setError('');
    setLoading(true);

    try {
      const cred = await signInWithPopup(auth, googleProvider);
      if (cred.user) {
        const token = await cred.user.getIdToken();
        localStorage.setItem('token', token);
        await syncUserToFirestore(cred.user);
        setUser(createAppUser(cred.user));
        setSubscription(createBasicSubscription());
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    if (!email) {
      setError('Please enter your email address.');
      setLoading(false);
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setMessage('Password reset email sent. Check your inbox.');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12">
      <div className="p-8 border rounded-lg bg-card">
        <h1 className="text-2xl font-bold text-center mb-6">
          {isForgotPassword ? 'Reset Password' : isRegister ? t('auth.register') : t('auth.login')}
        </h1>

        {error && (
          <div className="mb-4 p-3 text-sm bg-destructive/10 text-destructive rounded-lg">{error}</div>
        )}
        {message && (
          <div className="mb-4 p-3 text-sm bg-green-100 text-green-700 rounded-lg">{message}</div>
        )}

        {!isForgotPassword && (
          <>
            <button
              onClick={handleGoogle}
              disabled={loading}
              className="w-full px-4 py-3 border rounded-lg flex items-center justify-center gap-2 hover:bg-accent transition-colors mb-4"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.19 3.32v2.77h3.55c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.55-2.77c-.98.66-2.23 1.06-3.73 1.06-2.87 0-5.3-1.94-6.17-4.54H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.83 14.09a6.96 6.96 0 0 1 0-4.18V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.65 2.84c.87-2.6 3.3-4.53 6.17-4.53z"/></svg>
              {t('auth.googleSignIn')}
            </button>

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t"></div></div>
              <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">{t('auth.orContinueWith')}</span></div>
            </div>
          </>
        )}

        <form onSubmit={isForgotPassword ? handleForgotPassword : handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium">{t('auth.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>
          {!isForgotPassword && (
            <div>
              <label className="text-sm font-medium">{t('auth.password')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 disabled:opacity-50"
          >
            {loading
              ? t('common.loading')
              : isForgotPassword
                ? 'Send Reset Email'
                : isRegister
                  ? t('auth.register')
                  : t('auth.login')}
          </button>
        </form>

        {!isForgotPassword ? (
          <>
            <p className="mt-4 text-center text-sm text-muted-foreground">
              {isRegister ? t('auth.hasAccount') : t('auth.noAccount')}{' '}
              <button
                onClick={() => setIsRegister(!isRegister)}
                className="text-primary hover:underline"
              >
                {isRegister ? t('auth.loginNow') : t('auth.registerNow')}
              </button>
            </p>
            {!isRegister && (
              <p className="mt-2 text-center text-sm">
                <button
                  onClick={() => setIsForgotPassword(true)}
                  className="text-primary hover:underline"
                >
                  Forgot password?
                </button>
              </p>
            )}
          </>
        ) : (
          <p className="mt-4 text-center text-sm text-muted-foreground">
            <button
              onClick={() => setIsForgotPassword(false)}
              className="text-primary hover:underline"
            >
              Back to login
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
